import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Link } from '@tiptap/extension-link';
import { ResizableImage } from '../extensions/ResizableImageExtension';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Placeholder } from '@tiptap/extension-placeholder';
import { FontSize } from './extensions/FontSize';
import { CURATED_FONTS, FONT_SIZES } from '@/lib/fonts';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Code, Minus, Undo, Redo,
  Link2, Image as ImageIcon, Table as TableIcon,
  Highlighter, Palette, Plus, Trash2, Upload, Loader2,
  Subscript as SubscriptIcon, Superscript as SuperscriptIcon, RemoveFormatting,
  Check,
} from 'lucide-react';
import { IMAGE_SIZES } from './ResizableImage';
import { getApiBaseUrl } from '@/lib/apiBase';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const colors = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
  '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
  '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
];

const highlightColors = [
  '#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#FF0000', '#0000FF', '#FFA500', '#800080',
];

const DEFAULT_INSERT_SIZE = '320px';

// ── Shared image style helpers ────────────────────────────────────────────────

function parseImageStyle(style) {
  if (!style) return { width: null, align: 'none' }
  const wm = style.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)
  const fm = style.match(/(?:^|;)\s*float\s*:\s*([^;]+)/i)
  const width = wm ? wm[1].trim() : null
  const fv    = fm ? fm[1].trim() : null
  return { width, align: fv === 'left' ? 'left' : fv === 'right' ? 'right' : 'none' }
}

function buildImageStyle(width, align) {
  const base = `width: ${width}; max-width: 100%; height: auto;`
  if (align === 'left')  return `float: left; margin: 0 1rem 0.5rem 0; ${base}`
  if (align === 'right') return `float: right; margin: 0 0 0.5rem 1rem; ${base}`
  return base
}

// ── Image context toolbar (shown between main toolbar and editor when image selected) ─

const ImageContextToolbar = ({ editor, selectedImagePos }) => {
  const [customSize, setCustomSize] = useState('')

  if (selectedImagePos === null) return null

  const node         = editor.state.doc.nodeAt(selectedImagePos)
  const currentStyle = node?.attrs?.style ?? null
  const { width: currentWidth, align } = parseImageStyle(currentStyle)

  const applySize = (value) => {
    editor.chain()
      .setNodeSelection(selectedImagePos)
      .updateAttributes('image', { style: buildImageStyle(value, align) })
      .run()
    setCustomSize('')
  }

  const applyAlign = (newAlign) => {
    editor.chain()
      .setNodeSelection(selectedImagePos)
      .updateAttributes('image', { style: buildImageStyle(currentWidth || '100%', newAlign) })
      .run()
  }

  const applyCustom = () => {
    const v = customSize.trim()
    if (!v) return
    applySize(/^\d+$/.test(v) ? `${v}px` : v)
  }

  return (
    <div className="border-b bg-amber-50 dark:bg-amber-950/20 px-2 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      {/* Label */}
      <span className="text-[11px] font-semibold text-amber-800 dark:text-amber-400 shrink-0">
        Image:
      </span>

      {/* Size presets */}
      <div className="flex flex-wrap gap-1">
        {IMAGE_SIZES.map(({ label, value, title }) => (
          <button
            key={value}
            type="button"
            title={title}
            onClick={() => applySize(value)}
            className="text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors"
            style={{
              background:   currentWidth === value ? '#A67C52' : undefined,
              borderColor:  currentWidth === value ? '#A67C52' : undefined,
              color:        currentWidth === value ? '#fff'    : undefined,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Custom size */}
      <div className="flex items-center gap-1">
        <Input
          type="text"
          placeholder="Custom (e.g. 400px or 60%)"
          value={customSize}
          onChange={e => setCustomSize(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyCustom() }}
          className="h-6 text-[11px] w-36"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 text-[11px] px-2 shrink-0"
          disabled={!customSize.trim()}
          onClick={applyCustom}
        >
          Set
        </Button>
      </div>

      <Separator orientation="vertical" className="h-4 mx-0.5" />

      {/* Alignment */}
      <div className="flex items-center gap-1">
        {[
          { value: 'left',  icon: '←', title: 'Float left'  },
          { value: 'none',  icon: '↔', title: 'No float'    },
          { value: 'right', icon: '→', title: 'Float right' },
        ].map(({ value, icon, title }) => (
          <button
            key={value}
            type="button"
            title={title}
            onClick={() => applyAlign(value)}
            className="w-6 h-6 text-[11px] rounded border transition-colors"
            style={{
              background:  align === value ? '#A67C52' : undefined,
              borderColor: align === value ? '#A67C52' : undefined,
              color:       align === value ? '#fff'    : undefined,
            }}
          >
            {icon}
          </button>
        ))}
      </div>

      {/* Current size indicator */}
      <span className="text-[10px] text-muted-foreground ml-auto">
        {currentWidth || 'auto'}
      </span>
    </div>
  )
}

const MenuBar = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [insertSize, setInsertSize] = useState(DEFAULT_INSERT_SIZE);
  const [customInsertSize, setCustomInsertSize] = useState('');
  const fileInputRef = useRef(null);

  const setLink = useCallback(() => {
    if (!editor) return;
    const trimmed = linkUrl.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    let url = trimmed;
    if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) {
      url = `https://${url}`;
    }
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
        toast.error('Only http, https or mailto links are allowed');
        return;
      }
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkUrl('');
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    if (!editor || !imageUrl) return;
    editor.chain().focus().setImage({
      src: imageUrl,
      style: buildImageStyle(insertSize, 'none'),
    }).run();
    setImageUrl('');
  }, [editor, imageUrl, insertSize]);

  const handleLocalImageUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;
    // capture insertSize via closure (updated on each render via useCallback dep)

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/admin/upload/image`, formData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data',
        },
      });
      const relativeUrl = res.data?.url;
      if (!relativeUrl) throw new Error('Upload failed: no URL returned');
      const fullUrl = relativeUrl.startsWith('http')
        ? relativeUrl
        : `${API_URL.replace(/\/api$/, '')}${relativeUrl}`;
      editor.chain().focus().setImage({
        src: fullUrl,
        alt: file.name,
        style: buildImageStyle(insertSize, 'none'),
      }).run();
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [editor, insertSize]);

  const addTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border-b bg-muted/50 p-2 flex flex-wrap items-center gap-1">
      {/* Undo/Redo */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Font Family */}
      <Select
        value={editor.getAttributes('textStyle').fontFamily || 'Inter'}
        onValueChange={(value) => editor.chain().focus().setFontFamily(value).run()}
      >
        <SelectTrigger className="w-36 h-8 text-xs">
          <SelectValue placeholder="Font" />
        </SelectTrigger>
        <SelectContent>
          {CURATED_FONTS.map((font) => (
            <SelectItem key={font.value} value={font.value}>
              {font.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Font Size */}
      <Select
        value={editor.getAttributes('textStyle').fontSize || 'default'}
        onValueChange={(value) => {
          if (value === 'default') {
            editor.chain().focus().unsetFontSize().run();
          } else {
            editor.chain().focus().setFontSize(value).run();
          }
        }}
      >
        <SelectTrigger className="w-24 h-8 text-xs">
          <SelectValue placeholder="Size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">Default</SelectItem>
          {FONT_SIZES.map((size) => (
            <SelectItem key={size} value={`${size}px`}>
              {size}px
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Headings */}
      <Select
        value={
          editor.isActive('heading', { level: 1 }) ? 'h1' :
          editor.isActive('heading', { level: 2 }) ? 'h2' :
          editor.isActive('heading', { level: 3 }) ? 'h3' :
          editor.isActive('heading', { level: 4 }) ? 'h4' : 'p'
        }
        onValueChange={(value) => {
          if (value === 'p') {
            editor.chain().focus().setParagraph().run();
          } else {
            const level = parseInt(value.replace('h', ''));
            editor.chain().focus().toggleHeading({ level }).run();
          }
        }}
      >
        <SelectTrigger className="w-28 h-8 text-xs">
          <SelectValue placeholder="Style" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="p">Normal</SelectItem>
          <SelectItem value="h1">Heading 1</SelectItem>
          <SelectItem value="h2">Heading 2</SelectItem>
          <SelectItem value="h3">Heading 3</SelectItem>
          <SelectItem value="h4">Heading 4</SelectItem>
        </SelectContent>
      </Select>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Text Formatting */}
      <Button
        type="button"
        variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('underline') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('strike') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('subscript') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleSubscript().run()}
        title="Subscript"
      >
        <SubscriptIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('superscript') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
        title="Superscript"
      >
        <SuperscriptIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        title="Clear Formatting"
      >
        <RemoveFormatting className="w-4 h-4" />
      </Button>

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" title="Text Color">
            <Palette className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <div className="grid grid-cols-10 gap-1">
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                onClick={() => editor.chain().focus().setColor(color).run()}
              />
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full mt-2"
            onClick={() => editor.chain().focus().unsetColor().run()}
          >
            Remove Color
          </Button>
        </PopoverContent>
      </Popover>

      {/* Highlight */}
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" title="Highlight">
            <Highlighter className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <div className="grid grid-cols-4 gap-1">
            {highlightColors.map((color) => (
              <button
                key={color}
                type="button"
                className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
              />
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full mt-2"
            onClick={() => editor.chain().focus().unsetHighlight().run()}
          >
            Remove Highlight
          </Button>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Alignment */}
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'left' }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'center' }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'right' }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive({ textAlign: 'justify' }) ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        title="Justify"
      >
        <AlignJustify className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <Button
        type="button"
        variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>

      {/* Blockquote & Code */}
      <Button
        type="button"
        variant={editor.isActive('blockquote') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Quote"
      >
        <Quote className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('codeBlock') ? 'secondary' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        title="Code Block"
      >
        <Code className="w-4 h-4" />
      </Button>

      {/* Horizontal Rule */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Line"
      >
        <Minus className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Link */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={editor.isActive('link') ? 'secondary' : 'ghost'}
            size="sm"
            title="Insert Link"
          >
            <Link2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={setLink}>
                {editor.isActive('link') ? 'Update Link' : 'Add Link'}
              </Button>
              {editor.isActive('link') && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => editor.chain().focus().unsetLink().run()}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Image */}
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" title="Insert Image" data-testid="rte-image-btn">
            <ImageIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-72 max-w-[calc(100vw-1rem)]"
          align="start"
          sideOffset={6}
          avoidCollisions
          collisionPadding={8}
        >
          <div className="overflow-y-auto max-h-[min(80vh,520px)]">
            <div className="p-3 space-y-3">
              {/* Upload */}
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Upload from computer
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={handleLocalImageUpload}
                  data-testid="rte-image-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full mt-1.5"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="rte-image-upload-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Choose Image</>
                  )}
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG, WebP or GIF · max 10 MB</p>
              </div>

              <Separator />

              {/* URL */}
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Or paste image URL
                </Label>
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="mt-1.5 h-8 text-xs"
                  data-testid="rte-image-url-input"
                />
                <Button
                  type="button"
                  size="sm"
                  className="w-full mt-1.5 h-8 text-xs"
                  onClick={addImage}
                  disabled={!imageUrl}
                  data-testid="rte-image-url-insert-btn"
                >
                  Insert from URL
                </Button>
              </div>

              <Separator />

              {/* Size presets */}
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Initial size
                </Label>
                <div className="mt-1.5 grid grid-cols-5 gap-1">
                  {IMAGE_SIZES.map(({ label, value, title }) => (
                    <button
                      key={value}
                      type="button"
                      title={title}
                      onClick={() => setInsertSize(value)}
                      className="relative text-[11px] py-0.5 rounded border font-semibold transition-colors text-center leading-5"
                      style={{
                        background:  insertSize === value ? '#A67C52' : undefined,
                        borderColor: insertSize === value ? '#A67C52' : undefined,
                        color:       insertSize === value ? '#fff'    : undefined,
                      }}
                    >
                      {label}
                      {insertSize === value && (
                        <Check className="absolute top-0 right-0 w-2 h-2 -mt-0.5 -mr-0.5" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Custom size */}
                <div className="flex gap-1 mt-1.5">
                  <Input
                    type="text"
                    placeholder="Custom: 400px or 60%"
                    value={customInsertSize}
                    onChange={(e) => setCustomInsertSize(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customInsertSize.trim()) {
                        const v = customInsertSize.trim();
                        setInsertSize(/^\d+$/.test(v) ? `${v}px` : v);
                        setCustomInsertSize('');
                      }
                    }}
                    className="h-7 text-[11px]"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 text-[11px] px-2 shrink-0"
                    disabled={!customInsertSize.trim()}
                    onClick={() => {
                      const v = customInsertSize.trim();
                      if (!v) return;
                      setInsertSize(/^\d+$/.test(v) ? `${v}px` : v);
                      setCustomInsertSize('');
                    }}
                  >
                    Set
                  </Button>
                </div>

                <p className="text-[10px] text-muted-foreground mt-1 leading-4">
                  <span className="font-medium" style={{ color: '#A67C52' }}>
                    {IMAGE_SIZES.find(s => s.value === insertSize)?.title ?? insertSize}
                  </span>
                  {' '}· Drag edge to fine-tune after insertion.
                </p>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Table */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={editor.isActive('table') ? 'secondary' : 'ghost'}
            size="sm"
            title="Insert Table"
          >
            <TableIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto">
          <div className="space-y-2">
            <Button type="button" size="sm" className="w-full" onClick={addTable}>
              <Plus className="w-4 h-4 mr-2" /> Insert 3x3 Table
            </Button>
            {editor.isActive('table') && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                  >
                    Add Col Before
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                  >
                    Add Col After
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                  >
                    Add Row Before
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                  >
                    Add Row After
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                  >
                    Delete Column
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editor.chain().focus().deleteRow().run()}
                  >
                    Delete Row
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="col-span-2"
                    onClick={() => editor.chain().focus().deleteTable().run()}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Table
                  </Button>
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, placeholder = 'Write something...', className = '' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline cursor-pointer',
        },
      }),
      ResizableImage.configure({ inline: true, allowBase64: true }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'bg-muted font-bold',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-border p-2',
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      FontFamily,
      FontSize,
      Subscript,
      Superscript,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Track selected image node position for context toolbar
  const [selectedImagePos, setSelectedImagePos] = useState(null);
  useEffect(() => {
    if (!editor) return;
    const onSel = () => {
      const { selection } = editor.state;
      if (selection.node?.type.name === 'image') {
        setSelectedImagePos(selection.from);
      } else {
        setSelectedImagePos(null);
      }
    };
    editor.on('selectionUpdate', onSel);
    editor.on('update', onSel);
    return () => {
      editor.off('selectionUpdate', onSel);
      editor.off('update', onSel);
    };
  }, [editor]);

  // Drag-to-resize handle for images, rendered as a floating overlay OUTSIDE
  // editor.view.dom. A prior version wrapped each <img> in a wrapper <div>
  // inserted directly into editor.view.dom on every 'update'/'selectionUpdate'
  // event. That reparenting is a structural mutation inside ProseMirror's own
  // managed subtree, which ProseMirror's internal MutationObserver detects and
  // "corrects" (removing the non-schema wrapper) — sometimes as a real
  // transaction, which re-fires 'update', which re-wraps, which gets
  // corrected again, forever. That synchronous MutationObserver loop never
  // yields to the browser, freezing the tab — and only lessons with images
  // ever entered the wrapping code path, matching the reported symptom.
  // This version never touches editor.view.dom's structure, only reads
  // bounding boxes and (during an active drag) sets img.style.width, so
  // ProseMirror has nothing structural to "fix".
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;

    const handle = document.createElement('div');
    handle.style.cssText =
      'position:fixed;width:16px;height:16px;background:#A67C52;border-radius:3px;' +
      'cursor:se-resize;z-index:9999;display:none;align-items:center;justify-content:center;';
    handle.innerHTML =
      '<svg width="10" height="10" viewBox="0 0 10 10" fill="none">' +
      '<path d="M9 1L1 9M9 5L5 9M9 9H5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>';
    document.body.appendChild(handle);

    let hoveredImg = null;
    let dragging = false;

    // Clip to every scrollable/clipping ancestor (the RichTextEditor's own
    // overflow-y:auto div AND the Dialog's own overflow-y:auto wrapper, or
    // any other nesting) plus the viewport. This handle lives outside
    // editor.view.dom (position:fixed on document.body) so it isn't
    // clipped automatically like the image is. Without this, a tall/
    // scrolled image's handle can render below the modal's visible area,
    // over the dialog backdrop, and a click there closes the modal instead
    // of resizing.
    const getVisibleRect = (el) => {
      let rect = el.getBoundingClientRect();
      let node = el.parentElement;
      while (node) {
        const cs = getComputedStyle(node);
        if (/(auto|scroll|hidden|clip)/.test(cs.overflow + cs.overflowY + cs.overflowX)) {
          const n = node.getBoundingClientRect();
          rect = {
            left: Math.max(rect.left, n.left),
            top: Math.max(rect.top, n.top),
            right: Math.min(rect.right, n.right),
            bottom: Math.min(rect.bottom, n.bottom),
          };
          if (rect.right <= rect.left || rect.bottom <= rect.top) return null;
        }
        node = node.parentElement;
      }
      rect = {
        left: Math.max(rect.left, 0),
        top: Math.max(rect.top, 0),
        right: Math.min(rect.right, window.innerWidth),
        bottom: Math.min(rect.bottom, window.innerHeight),
      };
      if (rect.right <= rect.left || rect.bottom <= rect.top) return null;
      return rect;
    };

    const positionHandle = (img) => {
      const r = getVisibleRect(img);
      if (!r) {
        handle.style.display = 'none';
        return;
      }
      handle.style.left = `${r.right - 20}px`;
      handle.style.top = `${r.bottom - 20}px`;
      handle.style.display = 'flex';
    };

    const hideHandle = () => {
      if (dragging) return;
      handle.style.display = 'none';
      hoveredImg = null;
    };

    const onDomMouseMove = (e) => {
      if (dragging) return;
      const img = e.target.closest?.('img');
      if (img && dom.contains(img)) {
        hoveredImg = img;
        positionHandle(img);
      } else {
        hideHandle();
      }
    };

    const onScroll = () => {
      if (hoveredImg) positionHandle(hoveredImg);
    };

    const onHandleMouseLeave = (e) => {
      if (dragging) return;
      if (e.relatedTarget && dom.contains(e.relatedTarget)) return;
      hideHandle();
    };

    const onHandleMouseDown = (e) => {
      if (!hoveredImg) return;
      const img = hoveredImg;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      const startX = e.clientX;
      const startWidth = img.offsetWidth;

      const onMove = (ev) => {
        const newWidth = Math.max(80, startWidth + (ev.clientX - startX));
        img.style.width = newWidth + 'px';
        positionHandle(img);
      };

      const onUp = () => {
        dragging = false;
        try {
          const pos = editor.view.posAtDOM(img, 0);
          if (pos != null && pos >= 0) {
            const currentStyle = editor.state.doc.nodeAt(pos)?.attrs?.style;
            const { align } = parseImageStyle(currentStyle);
            editor.chain()
              .setNodeSelection(pos)
              .updateAttributes('image', {
                style: buildImageStyle(img.style.width, align),
              })
              .run();
          }
        } catch (_) {}
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    dom.addEventListener('mousemove', onDomMouseMove);
    // capture:true catches scroll on any nested scrollable ancestor (the
    // Dialog's own overflow-y:auto wrapper, this editor's, etc.) even
    // though 'scroll' doesn't bubble.
    document.addEventListener('scroll', onScroll, true);
    handle.addEventListener('mouseleave', onHandleMouseLeave);
    handle.addEventListener('mousedown', onHandleMouseDown);

    return () => {
      dom.removeEventListener('mousemove', onDomMouseMove);
      document.removeEventListener('scroll', onScroll, true);
      handle.removeEventListener('mouseleave', onHandleMouseLeave);
      handle.removeEventListener('mousedown', onHandleMouseDown);
      handle.remove();
    };
  }, [editor]);

  return (
    <div className={`rich-text-editor border rounded-lg [overflow:clip] relative ${className}`}>
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-10 bg-background">
        <MenuBar editor={editor} />
        {/* Context toolbar for selected image */}
        {editor && (
          <ImageContextToolbar editor={editor} selectedImagePos={selectedImagePos} />
        )}
      </div>

      {/* Scrollable content area */}
      <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
        <EditorContent editor={editor} className="bg-background" />
      </div>
      <style>{`
        .ProseMirror {
          min-height: 200px;
          padding: 1rem;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror table {
          border-collapse: collapse;
          margin: 1rem 0;
          overflow: hidden;
          table-layout: fixed;
          width: 100%;
        }
        .ProseMirror table td,
        .ProseMirror table th {
          border: 1px solid #e5e7eb;
          box-sizing: border-box;
          min-width: 1em;
          padding: 0.5rem;
          position: relative;
          vertical-align: top;
        }
        .ProseMirror table th {
          background-color: #f3f4f6;
          font-weight: bold;
        }
        .ProseMirror table .selectedCell {
          background-color: #e0f2fe;
        }
        /* Node-view wrapper created by ReactNodeViewRenderer */
        .ProseMirror [data-node-view-wrapper] {
          display: inline-block;
          max-width: 100%;
          vertical-align: middle;
        }
        .ProseMirror img {
          display: block;
          max-width: 100%;
          cursor: pointer;
          border-radius: 0.5rem;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #e5e7eb;
          padding-left: 1rem;
          margin-left: 0;
          font-style: italic;
        }
        .ProseMirror pre {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
        }
        .ProseMirror code {
          background-color: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 1.5rem 0;
        }
        .ProseMirror h1 { font-size: 2rem; font-weight: bold; margin: 1rem 0; }
        .ProseMirror h2 { font-size: 1.5rem; font-weight: bold; margin: 0.875rem 0; }
        .ProseMirror h3 { font-size: 1.25rem; font-weight: bold; margin: 0.75rem 0; }
        .ProseMirror h4 { font-size: 1.125rem; font-weight: bold; margin: 0.625rem 0; }
        .ProseMirror ul { list-style-type: disc; padding-left: 1.5rem; }
        .ProseMirror ol { list-style-type: decimal; padding-left: 1.5rem; }
        .ProseMirror ul li, .ProseMirror ol li { margin: 0.25rem 0; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
